import { Component, NgZone, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { CatalogType } from '../../../models/request-and-responses/rest-api/enums/catalogType.enum';
import { WithSearchBarAndSieve } from '../../../helpers/base-classes/withSearcBarAndSieve.class';
import { ICatalog } from '../../../models/request-and-responses/rest-api/interfaces/catalog';
import { ICatalogWithInfo } from '../../../models/request-and-responses/rest-api/interfaces/catalogWithInfo';
import { NgbModal, NgbModalConfig, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SieveOperator, SieveSortOrder, SieveString } from '../../../helpers/sieve/sieveString.class';
import { ToastrService } from 'ngx-toastr';
import { DataService } from '../../../services/data.service';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NotificationService } from '../../../services/notification.service';
import { DownloadType, FileDownloadService } from '../../../services/file-download.service';
import { AutocompleteService } from '../../../services/autocomplete.service';
import { PataviumRole } from '../../../models/request-and-responses/rest-api/enums/role.enum.model';
import { GlobalsService } from '../../../services/globals.service';
import { IFlatUser } from '../../../models/request-and-responses/rest-api/interfaces/flatUser';
import { Subscription } from 'rxjs/Subscription';
import { LocalStorageService } from '../../../services/local-storage.service';
import { IUserToLinkTo } from '../../../helpers/userToLinkTo.interface';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { catchError, finalize, first, map, take, tap } from 'rxjs/operators';
import { IShadowStorageFile } from '../../../models/request-and-responses/rest-api/interfaces/shadowStorageFile';
import { UtilsDateService } from '../../../helpers/utils-date.service';
import { SieveFilter, SieveSort } from '../../../helpers/sieve/sieve-filters-and-sorts';
import { AssocUserCatalog } from '../../../models/request-and-responses/rest-api/classes/assocUserCatalog';
import { LocalStorageKey } from '../../../models/frontend/local-storage.key';
import { SessionStorageService } from '../../../services/session-storage.service';
import { UserSessionInfo } from '../../../models/frontend/user.session-info';
import { AreraGeoPartitionName } from '../../../models/request-and-responses/rest-api/enums/AreraGeoPartitionName';
import { AreraFileType } from '../../../models/request-and-responses/rest-api/enums/AreraFileType';
import { ICatalogAreraGasGeoPartitionFileResp } from '../../../models/request-and-responses/rest-api/interfaces/catalogAreraGasGeoPartitionFileResp';
import { EMPTY } from 'rxjs';

class AreraFile {
    readonly id: number;
    readonly type: AreraFileType;
    readonly typeDescription: string;
    readonly name: string;

    constructor(id: number, type: AreraFileType, name: string) {
        this.id = id;
        this.type = type;
        this.name = name;
        this.typeDescription = AreraFile.get_type_description(type);
    }


    private static get_type_description(t: AreraFileType): string {
        switch (t) {
            case AreraFileType.Conf:
                return 'Scheda di confrontabilità';
            case AreraFileType.Sint:
                return 'Scheda sintetica';
            default:
                return 'File sconosciuto';
        }
    }

}

class AreraFilesByType {
    readonly partitionName: AreraGeoPartitionName;
    readonly partitionDescription: string;
    private _Sint: AreraFile = new AreraFile(null, AreraFileType.Sint, null);
    private _Conf: AreraFile = new AreraFile(null, AreraFileType.Conf, null);

    private static get_partition_description_by_name(name: AreraGeoPartitionName): string {
        switch (name) {
            case AreraGeoPartitionName.C:
                return 'Ambito centrale (Marche, Toscana, Umbria)';
            case AreraGeoPartitionName.NE:
                return 'Ambito nord-orientale (Emilia-Romagna, Friuli-Venezia Giulia, Lombardia, Trentino-Alto Adige, Veneto)';
            case AreraGeoPartitionName.NW:
                return 'Ambito nord-occidentale (Piemonte, Valle d’Aosta, Liguria)';
            case AreraGeoPartitionName.S:
                return 'Ambito meridionale (Calabria, Sicilia)';
            case AreraGeoPartitionName.SEC:
                return 'Ambito centro-sud orientale (Abruzzo, Basilicata, Molise, Puglia)';
            case AreraGeoPartitionName.SWC:
                return 'Ambito centro-sud occidentale (Campania, Lazio)';
            default:
                console.error(`Ambito ${name} sconosciuto`);
                return 'Ambito sconosciuto';
        }
    }

    public get_file_by_type(fType: AreraFileType): AreraFile {
        switch (fType) {
            case AreraFileType.Conf:
                return this._Conf;
            case AreraFileType.Sint:
                return this._Conf;
            default:
                console.error(`Unknown file type ${fType}`);
                return new AreraFile(null, null, null);
        }
    }

    get lst(): AreraFile[] {
        return [this._Sint, this._Conf].filter(x => x != null);
    }


    constructor(partitionName: AreraGeoPartitionName) {
        this.partitionName = partitionName;
        this.partitionDescription = AreraFilesByType.get_partition_description_by_name(partitionName);
    }

    push(f: ICatalogAreraGasGeoPartitionFileResp) {
        console.debug(`pushing arera file`, f);
        const areraFile = new AreraFile(f.fileId, f.fileType, f.fileName);
        switch (f.fileType) {
            case AreraFileType.Conf:
                this._Conf = areraFile;
                break;
            case AreraFileType.Sint:
                this._Sint = areraFile;
                break;
            default:
                console.error(`Unknown file type ${f.fileType}`);
        }
        console.debug(this._Sint, this._Conf);
    }

    remove_file_by_type(fileType: AreraFileType) {
        switch (fileType) {
            case AreraFileType.Conf:
                this._Conf = new AreraFile(null, AreraFileType.Conf, null);
                break;
            case AreraFileType.Sint:
                this._Sint = new AreraFile(null, AreraFileType.Sint, null);
                break;
        }
    }
}


class AreraFilesAggregator {
    readonly _C = new AreraFilesByType(AreraGeoPartitionName.C);
    readonly _NE = new AreraFilesByType(AreraGeoPartitionName.NE);
    readonly _NW = new AreraFilesByType(AreraGeoPartitionName.NW);
    readonly _S = new AreraFilesByType(AreraGeoPartitionName.S);
    readonly _SEC = new AreraFilesByType(AreraGeoPartitionName.SEC);
    readonly _SWC = new AreraFilesByType(AreraGeoPartitionName.SWC);


    constructor(files: ICatalogAreraGasGeoPartitionFileResp[]) {
        for (const f of files) {
            this.push(f);
        }
    }

    get lst() {
        return [
            this._C,
            this._NE,
            this._NW,
            this._S,
            this._SEC,
            this._SWC
        ];
    }

    public get_files_by_partition_name(partitionName: AreraGeoPartitionName) {
        return this.get_aggregation_by_partition(partitionName);
    }

    public get_aggregation_by_partition(partitionName: AreraGeoPartitionName) {
        switch (partitionName) {
            case AreraGeoPartitionName.C:
                return this._C;
            case AreraGeoPartitionName.NE:
                return this._NE;
            case AreraGeoPartitionName.NW:
                return this._NW;
            case AreraGeoPartitionName.S:
                return this._S;
            case AreraGeoPartitionName.SEC:
                return this._SEC;
            case AreraGeoPartitionName.SWC:
                return this._SWC;
            default:
                return new AreraFilesByType(partitionName);
        }
    }

    push(f: ICatalogAreraGasGeoPartitionFileResp) {
        this.get_aggregation_by_partition(f.areraGasGeoPartitionName).push(f);
    }

    remove(fileType: AreraFileType, partitionName: AreraGeoPartitionName) {
        this.get_aggregation_by_partition(partitionName).remove_file_by_type(fileType);
    }
}



enum PricelistAction {
    LinkOnePricelist = 'LINK_ONE',
    Info = 'INFO',
    LinkManyPricelists = 'LINK_MANY',
    CteFiles = 'CTE_FILES'
}

interface ISimplifiedCatalog {
    idCatalog: number;
    type: CatalogType;
    checked: boolean;
}

class SimplifiedCatalog implements ISimplifiedCatalog {
    idCatalog: number;
    type: CatalogType;
    checked = false;
    constructor(c: number, chk: boolean, type: CatalogType) {
        this.idCatalog = c;
        this.checked = chk;
        this.type = type;
    }
}

@Component({
    selector: 'app-pricelist',
    templateUrl: './pricelist.component.html',
    styleUrls: ['./pricelist.component.css']
})
export class PricelistComponent extends WithSearchBarAndSieve implements OnInit, OnDestroy {
    CatalogType = CatalogType;
    SieveSort = SieveSort;
    DownloadType = DownloadType;
    protected canLinkOrUnlink = true;
    selectedPricelists: ISimplifiedCatalog[] = [];
    checkAllPriceListButtonStatus = false;
    progressLink: number = null;
    pricelists: ICatalog[];
    allUsersICanSee: IFlatUser[] = [];
    pricelist: ICatalogWithInfo;
    modalRef: NgbModalRef;
    isInLoad = false;
    usersToLinkTo: IUserToLinkTo[] = [];
    checkAll = false;
    isInitFinished = true;
    pricelistSubscription: Subscription;
    private messagesSubscription: Subscription;
    isLoadingCte = false;
    usernameFilter: string;
    catalogFilter = SieveFilter.Catalog.Catalog;
    private sessionInfo: UserSessionInfo;
    areraGeoPartitionsFiles: AreraFilesAggregator;
    constructor(
        protected toastr: ToastrService,
        protected dataService: DataService,
        protected modalService: NgbModal,
        protected modalConfig: NgbModalConfig,
        protected config: NgSelectConfig,
        protected zone: NgZone,
        protected notification: NotificationService,
        protected fileDownloadService: FileDownloadService,
        protected ptautocomplete: AutocompleteService,
        protected ngxService: NgxUiLoaderService,
        protected dateTimeService: UtilsDateService,
        protected globals: GlobalsService,
        protected localStorage: LocalStorageService
    ) {
        super(
            [],
            [
                {
                    key: SieveSort.Catalog.ValidoFino,
                    order: SieveSortOrder.Desc
                }
            ],
            ptautocomplete,
            ngxService,
            localStorage
        );
        this.sessionInfo = SessionStorageService.sessionInfo;
        this.localStorageKey = LocalStorageKey.CatalogPageConfig;
        //set canLinkOrUnlink
        this.canLinkOrUnlink =
            SessionStorageService.sessionInfo.role === PataviumRole.BackOfficeOperator ||
            SessionStorageService.sessionInfo.role === PataviumRole.Agency ||
            SessionStorageService.sessionInfo.role === PataviumRole.AgencyOperator;

        this.loadConfiguration();

        this.pricelistSubscription = new Subscription();
    }

    ngOnInit() {
        super.ngOnInit();
        this.initData();
    }
    ngOnDestroy(): void {
        if (this.messagesSubscription != null) {
            this.messagesSubscription.unsubscribe();
        }
    }

    //GET DATA
    initData() {
        this.isInitFinished = false;
        //pricelists
        const stringPricelists = new SieveString(this.sieveConf);
        stringPricelists.addFilters(this.globals.pricelistToDateIntervalFilters());
        this.dataService.getPricelists(stringPricelists).subscribe(response => {
            this.rows = response.total;
            this.pricelists = response.payload;
            //create selectedrecord.
            this.selectedPricelists = [];
            this.pricelists.forEach(pl => {
                this.selectedPricelists.push(new SimplifiedCatalog(pl.id, false, pl.energyOrGas));
            });
            this.checkAllPriceListButtonStatus = false; //TODO: perché qui e non all'apertura del modal?
            this.isInitFinished = true;
        });
        //usersICanSee
        const usersString = new SieveString();
        usersString.setPageSize(1000);
        const _value = this._filterValueBasedOnRole;
        if (_value != null) {
            usersString.addFilters([
                {
                    key: SieveFilter.User.Role,
                    operator: SieveOperator.Equal,
                    value: _value
                },
                {
                    key: SieveFilter.User.IsDisabled,
                    operator: SieveOperator.Equal,
                    value: false
                }
            ]);
            this.dataService
                .getFlatUsers(usersString)
                .pipe(
                    tap(response => (this.allUsersICanSee = response.payload)),
                    tap(() => console.log(`this.allUsersICanSee`, this.allUsersICanSee.length))
                )
                .subscribe();
        }
    }
    private async _getLinkedPricelistById(pricelist: ICatalog): Promise<ICatalogWithInfo> {
        if (pricelist == null) {
            return null;
        }
        return this.dataService
            .getLinkedPricelistById(pricelist.id, pricelist.energyOrGas)
            .toPromise()
            .then(resp => resp.item)
            .catch(() => null);
    }

    //MODAL
    openSinglePricelistAssociation(htmlElem: TemplateRef<any>, pricelist: ICatalog) {
        const actionCatalog = PricelistAction.LinkOnePricelist;
        this._openModal(htmlElem, actionCatalog, pricelist);
    }

    openPricelistInfo(htmlElem: TemplateRef<any>, pricelist: ICatalog) {
        const actionCatalog = PricelistAction.Info;
        this._openModal(htmlElem, actionCatalog, pricelist);
    }

    openCteFiles(htmlElem: TemplateRef<any>, pricelist: ICatalog) {
        const actionCatalog = PricelistAction.CteFiles;
        this._openModal(htmlElem, actionCatalog, pricelist);
    }

    openMultiplePricelistAssociation(htmlElem: TemplateRef<any>) {
        const actionCatalog = PricelistAction.LinkManyPricelists;
        const pricelist = null;
        this._openModal(htmlElem, actionCatalog, pricelist);
    }
    private async _setVariablesBeforeOpeningModal(action: PricelistAction, pricelist: ICatalog) {
        console.log(`SETTING VARIABLES BEFORE OPENING MODAL`, action, pricelist);
        //set variables
        this.usernameFilter = null;
        this.pricelist = await this._getLinkedPricelistById(pricelist);
        switch (action) {
            case PricelistAction.Info:
                return;
            case PricelistAction.LinkOnePricelist:
                this.usersToLinkTo = [];
                this.allUsersICanSee.forEach(userICanSee => {
                    const status: boolean = this._isUserAssociatedWithPricelist(
                        userICanSee,
                        this.pricelist
                    );
                    this.usersToLinkTo.push({
                        idUser: userICanSee.id,
                        idRole: userICanSee.role,
                        username: userICanSee.username,
                        status: status
                    });
                });
                this.setCheckAll();
                return;
            case PricelistAction.LinkManyPricelists:
                this.usersToLinkTo = [];
                this.allUsersICanSee.forEach(userICanSee => {
                    this.usersToLinkTo.push({
                        idUser: userICanSee.id,
                        idRole: userICanSee.role,
                        username: userICanSee.username,
                        status: false
                    });
                });
                return;
            case PricelistAction.CteFiles:
                this.areraGeoPartitionsFiles = new AreraFilesAggregator(this.pricelist.catalog.areraGasGeoPartitionFiles);
        }
    }
    private _openModal(htmlElem: TemplateRef<any>, action: PricelistAction, pricelist: ICatalog) {
        console.log(`OPEN MODAL`, action, pricelist);
        //set variables
        this._setVariablesBeforeOpeningModal(action, pricelist).then(() => {
            this.modalRef = this.modalService.open(htmlElem, {
                centered: true,
                size: 'lg',
                windowClass: 'modal-xxl',
                backdrop: 'static',
                keyboard: false
            });
        });
    }
    closeModal() {
        this.modalService.dismissAll();
        this.checkAll = false; //tmp fix
    }
    async doConfirmLink(catalogInfo: ICatalogWithInfo) {
        let listIds = [];
        if (!catalogInfo) {
            listIds = this.selectedPricelists.filter(r => r.checked);
        } else {
            listIds.push(
                new SimplifiedCatalog(catalogInfo.catalog.id, true, catalogInfo.catalog.energyOrGas)
            );
        }
        const tolink = [],
            tounlink = [];
        this.usersToLinkTo.forEach(utl => {
            if (utl.status === true) {
                tolink.push({
                    idUser: utl.idUser,
                    idRole: utl.idRole
                });
            } else {
                tounlink.push({
                    idUser: utl.idUser,
                    idRole: utl.idRole
                });
            }
        });
        const totalCall = listIds.length * 2;
        let doneCall = 0;
        this.isInLoad = true;
        for (let i = 0; i < listIds.length; i++) {
            const pairUser = new AssocUserCatalog();
            pairUser.idCatalog = listIds[i].idCatalog;
            pairUser.type = listIds[i].type;
            pairUser.userRole = tolink;
            try {
                await this.dataService
                    .associatePricelist(pairUser)
                    .pipe(take(1))
                    .toPromise();
                doneCall++;
                this.progressLink = Math.ceil((doneCall / totalCall) * 100);
            } catch {
                this.progressLink = null;
                this.isInLoad = false;
                return;
            }
        }
        for (let i = 0; i < listIds.length; i++) {
            const pairUser = new AssocUserCatalog();
            pairUser.idCatalog = listIds[i].idCatalog;
            pairUser.type = listIds[i].type;
            pairUser.userRole = tounlink;
            await this.dataService
                .dissociatePricelist(pairUser)
                .pipe(take(1))
                .toPromise();
            doneCall++;
            this.progressLink = Math.ceil((doneCall / totalCall) * 100);
        }
        this.initData();
        this.toastr.success('Configurazione salvata correttamente', 'Successo', {
            closeButton: true
        });
        this.closeModal();
        this.progressLink = null;
        this.isInLoad = false;
    }

    //GENERAL
    shouldUserBeHidden(user: IUserToLinkTo): boolean {
        const status = user.status != null ? user.status : false;
        const username = user.username || null;
        const textFilter =
            this.usernameFilter != null && this.usernameFilter.length > 0
                ? this.usernameFilter
                : null;
        const result =
            status !== true &&
            textFilter != null &&
            username != null &&
            username.toLocaleLowerCase().includes(textFilter.toLocaleLowerCase()) === false;
        if (result === true) {
            console.log(`${user.idUser} will be hidden`, result);
        }
        return result;
    }
    private isUserVisible(user: IUserToLinkTo): boolean {
        return !this.shouldUserBeHidden(user);
    }
    setCheckAll() {
        const visibleUsers = this.usersToLinkTo.filter(user => this.isUserVisible(user));
        const areAllUsersSelected = visibleUsers.every(user => user.status === true);
        this.checkAll = visibleUsers.length > 0 && areAllUsersSelected === true;
    }
    toggleCheckAllVisibleUsers() {
        this.usersToLinkTo.map(utl => {
            if (this.isUserVisible(utl)) {
                utl.status = this.checkAll;
            }
        });
    }
    toggleCheckAllPricelists() {
        this.selectedPricelists.forEach(o => {
            o.checked = this.checkAllPriceListButtonStatus;
        });
    }
    private _isUserAssociatedWithPricelist(user: IFlatUser, pricelist: ICatalogWithInfo): boolean {
        let exists = false;
        const isCatalogOK: boolean = pricelist != null && pricelist.userWithRole != null;
        const isUSerOK: boolean = user != null && user.id != null;
        if (isCatalogOK && isUSerOK) {
            exists = pricelist.userWithRole.some(associatedUser => {
                return Number(associatedUser.idUser) === Number(user.id);
            });
        }
        return exists;
    }

    //VISIBILITY
    private get _filterValueBasedOnRole(): PataviumRole {
        switch (SessionStorageService.sessionInfo.role) {
            case PataviumRole.Agent:
                return null;
            case PataviumRole.Agency:
            case PataviumRole.AgencyOperator:
                return PataviumRole.Agent;
            case PataviumRole.BackOfficeOperator:
                return PataviumRole.Agency;
            default:
                console.error(`Role '${SessionStorageService.sessionInfo.role}' not handled.`);
                return null;
        }
    }
    get isVisibileLink() {
        return this.canLinkOrUnlink && this.selectedPricelists.some(o => o.checked);
    }
    get canViewWhoisLinked(): boolean {
        return (
            [
                PataviumRole.Agency,
                PataviumRole.BackOfficeOperator,
                PataviumRole.AgencyOperator
            ].indexOf(SessionStorageService.sessionInfo.role) > -1
        );
    }

    get canUploadCte() {
        return SessionStorageService.sessionInfo.role === PataviumRole.BackOfficeOperator;
    }

    public removeCteFile(storageFile: IShadowStorageFile) {
        if (!storageFile) {
            return;
        }
        this.dataService.removeCteFile(this.pricelist.catalog.id, storageFile.id).subscribe(() => {
            const indexEl = this.pricelist.cteStorageFiles.findIndex(r => r.id === storageFile.id);
            if (indexEl >= 0) {
                this.pricelist.cteStorageFiles.splice(indexEl, 1);
            }
        });
    }

    public uploadNewCteFile(target: HTMLInputElement) {
        console.log('Inserting cte...');
        const files = target.files;
        if (!files || !files.length) {
            console.log('no file to upload');
            return;
        }
        if (!this.pricelist.catalog.id) {
            console.log('No id found into catalog');
        }
        const body = new FormData();
        const f = files[0]; //take just the first element
        console.log('Append cteFile');
        body.append('cteFile', f, f.name);
        this.isLoadingCte = true;
        console.log('Call service cte file upload');
        this.dataService
            .uploadCtePriceList(body, this.pricelist.catalog.id)
            .pipe(
                first(),
                map(resp => {
                    const catalogInfo = resp.payload[0];
                    if (catalogInfo.cteStorageFiles && catalogInfo.cteStorageFiles.length) {
                        this.pricelist.cteStorageFiles = catalogInfo.cteStorageFiles;
                    } else {
                        this.pricelist.cteStorageFiles = [];
                    }
                }),
                finalize(() => {
                    this.isLoadingCte = false;
                    target.value = null;
                    this.initData();
                })
            )
            .subscribe();
    }

    public openDocumentPdf(fd, filename, typeDown: DownloadType = DownloadType.STREAM) {
        return this.fileDownloadService.downloadAndOpenDocument(fd, filename, typeDown);
    }

    showDocument(id: number, name: string) {
        this.fileDownloadService.downloadAndOpenDocument(id, name, DownloadType.PDFVIEWER);
    }

    uploadAreraFile(fileType: AreraFileType, element: EventTarget, catalogId: number, partitionName: AreraGeoPartitionName) {
        console.debug(`Uploading arera file.`, fileType, event);

        if (fileType == null) {
            console.error(`Uploading arera file. File type is null.`);
            return;
        }

        if (element == null) {
            console.error(`Uploading arera file. Input element is null.`);
            return;
        }
        const inputElement = element as HTMLInputElement;

        const file = inputElement.files[0];
        if (file == null) {
            console.error(`Uploading arera file. File is null.`);
            return;
        }

        if (catalogId == null) {
            console.error(`Uploading arera file. Catalog id is null.`);
            return;
        }

        if (partitionName == null) {
            console.error(`Uploading arera file. Partition name is null.`);
            return;
        }

        this.dataService.uploadAreraFile(file, fileType, catalogId, partitionName)
            .pipe(
                tap(resp => this.areraGeoPartitionsFiles.push(resp.payload)),
                catchError(err => {
                    console.error(`Error uploading arera file`, file.name, file.type, fileType, catalogId, partitionName, err);
                    this.toastr.error(`Errore durante il caricamento del file`);
                    return EMPTY;
                }),
                finalize(() => {
                    if (inputElement != null) {
                        inputElement.value = null;
                    }
                }))
            .subscribe();

    }

    removeAreraDocument(fileId: number, fileType: AreraFileType, catalogId: number, partitionName: AreraGeoPartitionName) {
        this.dataService.removeAreraFile(fileId, fileType, catalogId, partitionName)
            .pipe(
                tap(() => {
                    this.areraGeoPartitionsFiles.remove(fileType, partitionName);
                }),
                catchError(err => {
                console.error(`Error removing arera file`, fileId, fileType, catalogId, partitionName, err);
                this.toastr.error(`Errore durante la rimozione del file`);
                return EMPTY;
            }))
            .subscribe();
    }
}



// WEBPACK FOOTER //
// ./src/app/pages/common/pricelist/pricelist.component.ts